import { CURRENCY_LOCALES } from "@repo/components/src/utils/money";
import { isCurrencyCode } from "@repo/components/src/utils/money/currencies";

/** Keep only what a money amount can be made of. Typing a letter should do nothing. */
export const digitsOnly = (raw: string): string => raw.replace(/[^\d]/g, "");

/**
 * Group the raw digits the way the currency's locale would, without a currency
 * glyph — an `InputSlot` already shows one, so the field body stays plain digits
 * with separators, e.g. "1500000" -> "1.500.000".
 */
export const formatMoneyDigits = (digits: string, currency: string): string => {
  if (digits.length === 0) return "";
  const locale = isCurrencyCode(currency)
    ? CURRENCY_LOCALES[currency]
    : "es-CL";
  return new Intl.NumberFormat(locale, {
    style: "decimal",
    useGrouping: true,
    maximumFractionDigits: 0,
  }).format(Number(digits));
};

/**
 * How many decimals a price in this currency can carry (0 for CLP, 2 for USD/PEN…),
 * asked of `Intl` rather than listed here. `style: "currency"` is not available on every
 * engine (Hermes on iOS lacks it), and not knowing means whole units only — the safe
 * side, since a stray decimal is what the backend would reject.
 */
export const currencyDecimals = (currency: string): number => {
  try {
    return (
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
      }).resolvedOptions().maximumFractionDigits ?? 0
    );
  } catch {
    return 0;
  }
};

/**
 * What a decimal price box keeps of whatever was typed or pasted: digits and at most
 * one separator (`,` or `.`, written back as `.`), with the integer part and the
 * decimals capped. A bare separator reads as "0." so ".5" is not a lone dot. With no
 * decimals allowed this is just the digits, capped.
 */
export const normalizeDecimalInput = (
  raw: string,
  decimals: number,
  maxIntegerDigits: number,
): string => {
  if (decimals <= 0) return digitsOnly(raw).slice(0, maxIntegerDigits);
  const separator = raw.search(/[.,]/);
  if (separator === -1) return digitsOnly(raw).slice(0, maxIntegerDigits);
  const integer = digitsOnly(raw.slice(0, separator)).slice(
    0,
    maxIntegerDigits,
  );
  const fraction = digitsOnly(raw.slice(separator + 1)).slice(0, decimals);
  return `${integer || "0"}.${fraction}`;
};
